import React, { useMemo, useState, useRef, useEffect } from 'react';
import {
  CalendarIcon,
  FunnelIcon,
  TruckIcon,
  AdjustmentsHorizontalIcon,
  ChevronDownIcon,
  PencilIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { useCarContext } from '../../../contexts/CarContext';
import { useTranslation } from 'react-i18next';

// Helper functions
const getTypeIcon = (type) => {
  const iconProps = "h-5 w-5 text-white";
  return <CalendarIcon className={iconProps} />;
};
const getStatusIndicator = (status) => {
  switch (status) {
    case 'pending': return 'bg-amber-500';
    case 'confirmed': return 'bg-emerald-500';
    case 'expired': return 'bg-rose-500';
    case 'completed': return 'bg-emerald-500';
    case 'cancelled': return 'bg-slate-500';
    default: return 'bg-gray-400';
  }
};

const formatDate = (dateString) => {
  const options = { year: 'numeric', month: 'short', day: 'numeric' };
  return new Date(dateString).toLocaleDateString('ro-RO', options);
};

const DriverAppointmentsHistory = () => {
  const { t } = useTranslation();
  const [dateSort, setDateSort] = useState('desc');
  const [activeFilter, setActiveFilter] = useState('all');
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);
  const { appointments, setAppointments } = useCarContext();
  const filterDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target)) {
        setIsFilterDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [filterDropdownRef]);

  const getStatusText = (status) => {
    const key = `appointmentsHistory.status.${status}`;
    const translation = t(key);
    return translation === key ? status : translation;
  };

  // Handler for delete
  const handleDelete = (id) => {
    setAppointments(prev => prev.filter(a => a.id !== id));
  };

  // Handler for edit (placeholder)
  const handleEdit = (appointment) => {
    // TODO: open modal or inline edit
    alert(`Editează programarea: ${appointment.service}`);
  };

  // Filter and sort appointments
  const filteredAppointments = useMemo(() => {
    let filtered = appointments;

    // Apply filter
    if (activeFilter === 'future') {
      filtered = filtered.filter(a => new Date(a.date) > new Date() && a.status !== 'cancelled');
    } else if (activeFilter === 'past') {
      filtered = filtered.filter(a => new Date(a.date) <= new Date() || a.status === 'cancelled');
    } else if (activeFilter === 'cancelled') {
      filtered = filtered.filter(a => a.status === 'cancelled');
    }

    // Sort by date
    return filtered.sort((a, b) => {
      if (dateSort === 'desc') {
        return new Date(b.date) - new Date(a.date);
      } else {
        return new Date(a.date) - new Date(b.date);
      }
    });
  }, [appointments, activeFilter, dateSort]);

  const getFilterName = (filter) => {
    const key = `appointmentsHistory.filterTypes.${filter}`;
    const translation = t(key);
    return translation === key ? filter : translation;
  };

  return (
    <div className="space-y-6">
      <div className="bg-slate-100 dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700">
        {/* Header */}
        <div className="px-2 py-2 border-b border-slate-200 dark:border-slate-700 bg-purple-50 dark:bg-purple-900/20 rounded-t-xl">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <CalendarIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                {t('appointmentsHistory.title')}
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {t('appointmentsHistory.subtitle')}
              </p>
            </div>
          </div>
        </div>

        {/* Filter and Sort Bar */}
        <div className="px-2 py-4 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center justify-between gap-3">
            <div ref={filterDropdownRef} className="relative inline-block text-left">
              <button
                type="button"
                onClick={() => setIsFilterDropdownOpen(!isFilterDropdownOpen)}
                className="inline-flex w-full justify-center items-center gap-x-1.5 rounded-md bg-slate-100 dark:bg-slate-700 px-3 py-2 text-xs font-medium text-slate-700 dark:text-slate-300 shadow-sm hover:bg-gray-200 dark:hover:bg-gray-600"
                aria-expanded={isFilterDropdownOpen}
                aria-haspopup="true"
              >
                <AdjustmentsHorizontalIcon className="h-4 w-4 mr-1 text-slate-500 dark:text-slate-400" aria-hidden="true" />
                {t('appointmentsHistory.filter')}: {getFilterName(activeFilter)}
                <ChevronDownIcon className={`-mr-1 h-4 w-4 text-slate-500 dark:text-slate-400 ${isFilterDropdownOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
              </button>
              {isFilterDropdownOpen && (
                <div className="absolute left-0 z-10 mt-2 w-36 origin-top-right rounded-md bg-white dark:bg-slate-800 shadow-lg ring-1 ring-black ring-opacity-5 dark:ring-gray-700 focus:outline-none">
                  <div className="py-1" role="none">
                    {['all', 'future', 'past', 'cancelled'].map((filterType) => (
                      <button
                        key={filterType}
                        onClick={() => { setActiveFilter(filterType); setIsFilterDropdownOpen(false); }}
                        className={`group flex w-full items-center px-4 py-2 text-sm ${activeFilter === filterType ? 'bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300' : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-gray-700'}`}
                        role="menuitem"
                        tabIndex="-1"
                      >
                        {getFilterName(filterType)}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <button
              onClick={() => setDateSort(dateSort === 'desc' ? 'asc' : 'desc')}
              className="flex items-center px-3 py-2 bg-slate-100 dark:bg-slate-700 rounded-md text-xs text-slate-700 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              <FunnelIcon className="h-4 w-4 mr-1 text-slate-500 dark:text-slate-400" />
              <span>{dateSort === 'desc' ? t('appointmentsHistory.sort.newest') : t('appointmentsHistory.sort.oldest')}</span>
            </button>
          </div>
        </div>

        {/* Appointments List */}
        <div className="px-2 py-4">
          <div className="space-y-3">
            {filteredAppointments.length > 0 ? (
              filteredAppointments.map(appointment => (
                <div
                  key={appointment.id}
                  className="p-3 rounded-lg flex items-start gap-3 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-gray-700/50"
                >
                  {/* Icon */}
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-purple-700 flex items-center justify-center mt-0.5">
                    {getTypeIcon('service')}
                  </div>
                  {/* Content */}
                  <div className="flex-1">
                    <div className="flex justify-between items-start mb-1">
                      <h4 className="text-sm font-semibold text-slate-900 dark:text-white">
                        {appointment.service}
                      </h4>
                      {/* Status Dot */}
                      {/* <div className="flex items-center text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                        <span className={`w-2 h-2 rounded-full mr-1.5 ${getStatusIndicator(appointment.status)}`}></span>
                        {getStatusText(appointment.status)}
                      </div> */}
                    </div>
                    <p className="text-sm text-slate-600 dark:text-slate-300">
                      {appointment.shopName || appointment.serviceName} - {appointment.location || appointment.shopAddress}
                    </p>
                    {/* Meta Info */}
                    <div className="mt-2 flex flex-wrap items-center text-xs text-slate-500 dark:text-slate-400 gap-x-3 gap-y-1">
                      <span className="flex items-center">
                        <TruckIcon className="h-3.5 w-3.5 mr-1 text-slate-400" />
                        {appointment.carName} ({appointment.carPlate})
                      </span>
                      <span className="flex items-center">
                        <CalendarIcon className="h-3.5 w-3.5 mr-1 text-slate-400" />
                        {formatDate(appointment.date)}
                      </span>
                    </div>
                  </div>
                  {/* Edit/Delete Buttons */}
                  {/* <div className="flex flex-col gap-2 ml-2">
                    <button
                      onClick={() => handleEdit(appointment)}
                      className="p-1.5 rounded-md bg-slate-100 dark:bg-slate-700 hover:bg-purple-100 dark:hover:bg-purple-900/40 text-slate-600 dark:text-slate-300"
                      title={t('appointmentsHistory.editAppointment') || 'Editează'}
                    >
                      <PencilIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(appointment.id)}
                      className="p-1.5 rounded-md bg-slate-100 dark:bg-slate-700 hover:bg-red-100 dark:hover:bg-red-900/40 text-rose-600 dark:text-red-400"
                      title={t('appointmentsHistory.deleteAppointment') || 'Șterge'}
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div> */}
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                <CalendarIcon className="h-10 w-10 mx-auto text-slate-400 dark:text-slate-500 mb-2" />
                {t('appointmentsHistory.noPastAppointments')}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DriverAppointmentsHistory;
